/* ==========================================================================
   Satjeevan — Main Product Information buy-box JS (main-product-buybox.js)

   Everything about Add to Cart / quantity / the price container itself is
   already handled automatically by Horizon's existing <product-form-component>
   and <product-price> (assets/product-form.js, assets/product-price.js) once
   variant-picker.js dispatches its ProductSelectEvent — no code needed here
   for those.

   The only two pieces of this buy-box NOT covered by an existing component
   are the Size/Weight unit shown next to the title and the "SAVE X%" badge.
   Both are synced here using the exact same technique product-price.js
   already uses: read the freshly fetched section HTML off the resolved
   event promise and copy the matching element over — no separate fetch,
   no guessing at variant JSON shapes.
   ========================================================================== */

import { StandardEvents } from '@shopify/events';

/**
 * @param {Document} html
 * @param {string} selector
 */
function syncUnit(html, selector) {
  const current = document.querySelector(selector);
  const updated = html.querySelector(selector);
  if (current && updated) {
    current.textContent = updated.textContent;
  }
}

/**
 * Syncs an element that may appear/disappear entirely (the save badge only
 * exists while the selected variant is on sale).
 * @param {Document} html
 * @param {string} selector
 * @param {string} anchorSelector - element to insert after when the badge needs to be (re)created
 */
function syncOptional(html, selector, anchorSelector) {
  const current = document.querySelector(selector);
  const updated = html.querySelector(selector);

  if (updated) {
    if (current) {
      current.replaceWith(updated.cloneNode(true));
    } else {
      document.querySelector(anchorSelector)?.insertAdjacentElement('afterend', updated.cloneNode(true));
    }
  } else if (current) {
    current.remove();
  }
}

/**
 * @param {CustomEvent & { promise?: Promise<{ detail?: { html?: Document } }>, target: EventTarget | null }} event
 */
function handleProductSelect(event) {
  if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
  if (!event.promise) return;

  event.promise
    .then((result) => {
      const html = result?.detail?.html;
      if (!html) return;

      syncUnit(html, '[data-sj-mpi-unit]');
      syncOptional(html, '[data-sj-mpi-save-badge]', '.sj-mpi-price');
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[main-product-buybox] Event promise rejected:', error);
    });
}

document.querySelectorAll('.shopify-section').forEach((section) => {
  if (!section.querySelector('.sj-mpi-buy-box')) return;
  section.addEventListener(StandardEvents.productSelect, handleProductSelect);
});
